import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Zap, Layers, Server, Shield } from 'lucide-react';

const content = {
  en: {
    label: 'Engineering Capabilities',
    title: 'Engineering at Production Scale',
    tagline: 'Built for reliability, scale, and long-term operational stability.',
    blocks: [
      {
        icon: Zap,
        title: 'Low-Latency Architectures',
        description: 'Performance engineered for real-time systems.'
      },
      {
        icon: Layers,
        title: 'Scalable System Design',
        description: 'Built to handle growth without re-architecture.'
      },
      {
        icon: Server,
        title: 'Production Infrastructure',
        description: 'Cloud-native, observable, and fault-tolerant.'
      },
      {
        icon: Shield,
        title: 'Risk-Aware Engineering',
        description: 'Structured development for mission-critical systems.'
      }
    ]
  },
  tr: {
    label: 'Mühendislik Yetenekleri',
    title: 'Production Ölçeğinde Mühendislik',
    tagline: 'Güvenilirlik, ölçeklenebilirlik ve uzun vadeli operasyonel stabilite için inşa edildi.',
    blocks: [
      {
        icon: Zap,
        title: 'Düşük Gecikmeli Mimariler',
        description: 'Gerçek zamanlı sistemler için performans mühendisliği.'
      },
      {
        icon: Layers,
        title: 'Ölçeklenebilir Sistem Tasarımı',
        description: 'Yeniden mimari olmadan büyümeyi kaldıracak şekilde inşa edildi.'
      },
      {
        icon: Server,
        title: 'Production Altyapısı',
        description: 'Cloud-native, gözlemlenebilir ve hata toleranslı.'
      },
      {
        icon: Shield,
        title: 'Risk-Bilinçli Mühendislik',
        description: 'Kritik sistemler için yapılandırılmış geliştirme.'
      }
    ]
  },
  de: {
    label: 'Engineering-Fähigkeiten',
    title: 'Engineering im Produktionsmaßstab',
    tagline: 'Gebaut für Zuverlässigkeit, Skalierbarkeit und langfristige operative Stabilität.',
    blocks: [
      {
        icon: Zap,
        title: 'Low-Latency-Architekturen',
        description: 'Performance-Engineering für Echtzeitsysteme.'
      },
      {
        icon: Layers,
        title: 'Skalierbares Systemdesign',
        description: 'Gebaut für Wachstum ohne Re-Architektur.'
      },
      {
        icon: Server,
        title: 'Produktionsinfrastruktur',
        description: 'Cloud-native, observierbar und fehlertolerant.'
      },
      {
        icon: Shield,
        title: 'Risikobewusstes Engineering',
        description: 'Strukturierte Entwicklung für geschäftskritische Systeme.'
      }
    ]
  }
};

export function AuthorityStrip() {
  const { currentLanguage } = useLanguage();
  const c = content[currentLanguage] || content.en;

  return (
    <section 
      className="py-24 relative bg-white"
      data-testid="authority-strip"
    >
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          {/* Label - with proper spacing */}
          <span 
            className="text-xs uppercase mb-5 block"
            style={{ 
              color: '#0F766E',
              letterSpacing: '0.15em'
            }}
          >
            {c.label}
          </span>
          
          {/* Title - weight 500, slight negative tracking */}
          <h2 
            className="text-2xl md:text-4xl mb-5"
            style={{ 
              color: 'var(--text-on-light-primary)',
              lineHeight: 1.2,
              fontWeight: 500,
              letterSpacing: '-0.01em'
            }}
          >
            {c.title}
          </h2>
          
          {/* Tagline */}
          <p 
            className="text-base md:text-lg max-w-[640px] mx-auto"
            style={{ color: 'var(--text-on-light-muted)' }}
          >
            {c.tagline}
          </p>
        </div>

        {/* 4 Blocks Grid - increased mobile gap */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-6 lg:gap-10">
          {c.blocks.map((block, idx) => {
            const IconComponent = block.icon;
            
            return (
              <div
                key={idx}
                className="p-6 lg:p-8 rounded-xl transition-colors duration-300"
                style={{ 
                  background: '#F8FAFB',
                  border: '1px solid rgba(15, 118, 110, 0.1)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(15, 118, 110, 0.06)';
                  e.currentTarget.style.borderColor = 'rgba(15, 118, 110, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#F8FAFB';
                  e.currentTarget.style.borderColor = 'rgba(15, 118, 110, 0.1)';
                }}
              >
                {/* Icon Container */}
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center mb-4"
                  style={{ background: 'rgba(15, 118, 110, 0.1)' }}
                >
                  <IconComponent 
                    className="w-[18px] h-[18px]" 
                    style={{ color: '#0F766E' }}
                    strokeWidth={1.5}
                  />
                </div>
                
                {/* Title */}
                <h3 
                  className="text-lg font-medium mb-3"
                  style={{ color: 'var(--text-on-light-primary)' }}
                >
                  {block.title}
                </h3>
                
                {/* Description */}
                <p 
                  className="text-sm"
                  style={{ 
                    color: 'var(--text-on-light-muted)',
                    lineHeight: 1.6
                  }}
                >
                  {block.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Subtle bottom divider */}
      <div 
        className="absolute bottom-0 left-0 right-0 h-px"
        style={{ background: 'rgba(15, 118, 110, 0.08)' }}
      />
    </section>
  );
}
